import React, { useCallback } from 'react';

import { CheckOutlined } from '@ant-design/icons';
import { Empty, Tree } from 'antd';
import type { AntTreeNodeProps, DataNode, TreeProps } from 'antd/es/tree';

import { COLOR } from '@/shared/design-tokens/design-tokens';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { useTreeSearch } from '@/shared/hooks/useTreeSearch';
import { VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { getNodeKey } from '@/shared/lib/tree';
import type { DomainEditHook } from '@/shared/types/editHooks';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { TreeNodeTitle } from '@/shared/ui/TreeNodeTitle';

import styles from './DomainEditPopover.module.css';

export type DomainEditPopoverProps = {
  domainEdit: DomainEditHook;
};

export const DomainEditPopover: React.FC<DomainEditPopoverProps> = ({ domainEdit }) => {
  const { treeData, selectedDomainId, isLoadingDomains, handleSelect, handleSave, handleClose } = domainEdit;

  const { searchTerm, setSearchTerm, filteredTreeData, allExpandedKeys, isChildOfMatchedParent } =
    useTreeSearch(treeData);

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const renderTreeNodeTitle = useCallback(
    (nodeData: DataNode) => {
      const nodeKey = getNodeKey(nodeData);
      const isHighlighted = isChildOfMatchedParent(nodeKey);
      const isSelected = selectedDomainId === nodeKey;

      return (
        <div className={styles.treeNodeTitle}>
          <TreeNodeTitle nodeData={nodeData} isHighlighted={isHighlighted} />
          {isSelected && <CheckOutlined className={styles.checkIcon} />}
        </div>
      );
    },
    [isChildOfMatchedParent, selectedDomainId]
  );

  const switcherIcon = (nodeProps: AntTreeNodeProps) => {
    const { expanded } = nodeProps;
    return expanded ? (
      <Icon name="tree-collapse" size={16} color={COLOR.GRAY[0]} />
    ) : (
      <Icon name="tree-expand" size={16} color={COLOR.GRAY[0]} />
    );
  };

  const treeProps: TreeProps = {
    treeData: filteredTreeData,
    selectedKeys: selectedDomainId ? [selectedDomainId] : [],
    onSelect: async (selectedKeys) => {
      if (selectedKeys.length > 0) {
        const domainId = selectedKeys[0] as string;
        handleSelect(selectedKeys);
        handleClose();
        await handleSave([domainId]);
      }
    },
    switcherIcon,
    className: styles.tree,
    expandedKeys: allExpandedKeys,
    titleRender: renderTreeNodeTitle,
  };

  return (
    <div className={styles.popover}>
      <div className={styles.searchContainer}>
        <SearchInput
          prefix={<Icon name="search" size={13} />}
          placeholder="도메인 검색"
          value={searchTerm}
          onChange={(e) => {
            const validatedValue = validateLength(e.target.value);
            setSearchTerm(validatedValue);
          }}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          allowClear
          maxLength={30}
          className={styles.searchInput}
        />
      </div>
      <div className={styles.treeContainer}>
        <div className={styles.treeWrapper}>
          {isLoadingDomains ? (
            <LoadingState message="" size={32} />
          ) : filteredTreeData.length > 0 ? (
            <Tree {...treeProps} />
          ) : (
            <div className={styles.emptyWrapper}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={searchTerm ? '검색 결과가 없습니다' : '도메인이 없습니다'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
